import { useEffect, useRef } from 'react';

import {
  decidePollTick,
  isDocumentHidden,
  type PollTrigger,
} from '../lib/polling/pollGate';

export interface GatedPollOptions {
  /** Poll only while true: turning it on starts the interval, off stops it. */
  active: boolean;
  intervalMs: number;
  /**
   * An in-flight request at least this old no longer blocks a tick (so a hung
   * request cannot freeze the poll). Default: an in-flight request always blocks.
   */
  staleInFlightMs?: number;
  /** Start time (epoch ms) of the request in flight, or null. Default: none tracked. */
  getInFlightSince?: () => number | null;
  /**
   * Start time (epoch ms) of the most recent fetch from ANY trigger (one-off
   * refetches included). Default: this hook's own last fetch, or activation.
   */
  getLastFetchAt?: () => number | null;
  /** Issue one fetch. */
  onFetch: () => void;
}

/**
 * Interval poll gated by the caller's activity flag, paused while the tab is
 * hidden (one catch-up fetch when it becomes visible again after missing a
 * tick), and never overlapping an in-flight request. The decision itself is
 * the pure `decidePollTick`; this hook only wires timers and the
 * `visibilitychange` listener. Callbacks are read through a ref, so changing
 * them does not restart the interval — only `active`/`intervalMs` do.
 */
export function useGatedPoll(options: GatedPollOptions): void {
  const latest = useRef(options);
  useEffect(() => {
    latest.current = options;
  });

  const { active, intervalMs } = options;
  const staleInFlightMs = options.staleInFlightMs ?? Number.POSITIVE_INFINITY;

  useEffect(() => {
    if (!active) return undefined;
    // Activation counts as fresh: whatever made the poll active was just read.
    let lastFiredAt = Date.now();
    const tick = (trigger: PollTrigger) => {
      const current = latest.current;
      const now = Date.now();
      const inFlightSince = current.getInFlightSince?.() ?? null;
      const lastFetchAt = Math.max(
        lastFiredAt,
        current.getLastFetchAt?.() ?? Number.NEGATIVE_INFINITY,
      );
      const decision = decidePollTick({
        trigger,
        active: true,
        hidden: isDocumentHidden(),
        inFlightForMs: inFlightSince === null ? null : now - inFlightSince,
        msSinceLastFetch: now - lastFetchAt,
        intervalMs,
        staleInFlightMs,
      });
      if (decision !== 'fetch') return;
      lastFiredAt = now;
      current.onFetch();
    };
    const timer = window.setInterval(() => tick('interval'), intervalMs);
    const onVisibilityChange = () => {
      if (!isDocumentHidden()) tick('visible');
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [active, intervalMs, staleInFlightMs]);
}
